import { useSubscription, type SubscriptionPlan } from "./useSubscription";
import { useRoles } from "./useRoles";

/**
 * Feature definitions with their required plan and upgrade messaging.
 */
export type Feature =
  | "barcode_scanner"
  | "workout_styles"
  | "unlimited_templates"
  | "unlimited_history"
  | "progress_charts"
  | "ai_chat"
  | "ai_plans"
  | "plateau_alerts"
  | "coach_assignment"
  | "coach_messaging"
  | "weekly_checkin";

interface FeatureConfig {
  requiredPlan: SubscriptionPlan;
  label: string;
  upgradeTitle: string;
  upgradeDescription: string;
}

const FEATURE_MAP: Record<Feature, FeatureConfig> = {
  barcode_scanner: {
    requiredPlan: "track",
    label: "Barcode Scanner",
    upgradeTitle: "Unlock Barcode Scanning",
    upgradeDescription: "Scan barcodes to quickly log food. Upgrade to Track to unlock this feature.",
  },
  workout_styles: {
    requiredPlan: "track",
    label: "Workout Styles",
    upgradeTitle: "Unlock All Workout Styles",
    upgradeDescription: "Access Bodybuilding, Powerlifting, HIIT, and Strongman styles. Upgrade to Track.",
  },
  unlimited_templates: {
    requiredPlan: "track",
    label: "Unlimited Templates",
    upgradeTitle: "Unlimited Workout Templates",
    upgradeDescription: "Free users can save up to 3 templates. Upgrade to Track for unlimited templates.",
  },
  unlimited_history: {
    requiredPlan: "track",
    label: "Full History",
    upgradeTitle: "Unlock Full Workout History",
    upgradeDescription: "Free users can view the last 30 days. Upgrade to Track for unlimited history.",
  },
  progress_charts: {
    requiredPlan: "track",
    label: "Progress Charts",
    upgradeTitle: "Unlock Progress Charts",
    upgradeDescription: "Visualize your strength and volume trends. Upgrade to Track to unlock charts.",
  },
  ai_chat: {
    requiredPlan: "ai_coach",
    label: "AI Coach Chat",
    upgradeTitle: "Get AI Coaching",
    upgradeDescription: "Chat with an AI coach for personalized workout and nutrition advice. Upgrade to AI Coach.",
  },
  ai_plans: {
    requiredPlan: "ai_coach",
    label: "AI Plans",
    upgradeTitle: "AI-Generated Plans",
    upgradeDescription: "Get custom workout and nutrition plans built by AI. Upgrade to AI Coach.",
  },
  plateau_alerts: {
    requiredPlan: "ai_coach",
    label: "Plateau Detection",
    upgradeTitle: "Plateau Detection",
    upgradeDescription: "Get alerted when your progress stalls. Upgrade to AI Coach.",
  },
  coach_assignment: {
    requiredPlan: "coach",
    label: "Personal Coach",
    upgradeTitle: "Get a Personal Coach",
    upgradeDescription: "Work with a real human coach who builds plans and holds you accountable. Upgrade to Coach.",
  },
  coach_messaging: {
    requiredPlan: "coach",
    label: "Coach Messaging",
    upgradeTitle: "Direct Coach Messaging",
    upgradeDescription: "Message your coach directly within the app. Upgrade to Coach.",
  },
  weekly_checkin: {
    requiredPlan: "coach",
    label: "Weekly Check-ins",
    upgradeTitle: "Weekly Check-ins",
    upgradeDescription: "Structured weekly check-ins reviewed by your coach. Upgrade to Coach.",
  },
};

const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  free: "Free",
  track: "Track ($14.99/mo)",
  ai_coach: "AI Coach ($29.99/mo)",
  coach: "Coach ($149.99/mo)",
  partner: "Partner",
};

export function useFeatureGate() {
  const { plan, hasAccess, loading } = useSubscription();

  const { isAdmin, isCoach } = useRoles();

  const canAccess = (feature: Feature): boolean => {
    if (isAdmin || isCoach) return true;
    return hasAccess(FEATURE_MAP[feature].requiredPlan);
  };

  const getUpgradeInfo = (feature: Feature) => {
    const config = FEATURE_MAP[feature];
    return {
      ...config,
      requiredPlanLabel: PLAN_LABELS[config.requiredPlan],
      currentPlanLabel: PLAN_LABELS[plan],
    };
  };

  /** Check template limit for free users */
  const canCreateTemplate = (currentCount: number): boolean => {
    if (isAdmin || isCoach) return true;
    if (hasAccess("track")) return true;
    return currentCount < 3;
  };

  /** Check history depth for free users (30 days) */
  const getHistoryCutoff = (): Date | null => {
    if (isAdmin || isCoach) return null;
    if (hasAccess("track")) return null; // unlimited
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return cutoff;
  };

  return {
    plan,
    loading,
    isAdmin,
    isCoach,
    canAccess,
    getUpgradeInfo,
    canCreateTemplate,
    getHistoryCutoff,
    planLabel: PLAN_LABELS[plan],
  };
}
